import type { ReactNode } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import ArticleHero from '../../../../components/news/ArticleHero';
import PullQuote from '../../../../components/news/PullQuote';
import KeyPointsBox from '../../../../components/news/KeyPointsBox';
import CommentBox from '../../../../components/news/CommentBox';
import SourceCard from '../../../../components/news/SourceCard';
import RelatedCards from '../../../../components/news/RelatedCards';

/* ---- In-article inline parts (editorial layout; no section bands) ---- */
function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}
function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        Source: {media} (<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>)
      </figcaption>
    </figure>
  );
}
function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}
const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function ChinaButlerDemandSurgePageEN() {
  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'News', href: '/en/news' },
    { label: 'Surge in Butler Demand Among Chinese Wealthy' }
  ];

  const article = {
    category: 'Emerging Markets & Growth Trends',
    date: 'December 4, 2024',
    title: 'Surge in Butler Demand Among Chinese Wealthy',
    subtitle: "Rapid Growth of Butler Services in Asia's Largest Market",
    heroImage: '/images/generated/jba-fs-060-news_china-butler-demand-surge.jpg',
    lead: "Demand for private butler services is surging among China's wealthy, with companies managing approximately 1,000 butlers emerging. The domestic service industry's market size exceeds 1 trillion yuan, with demand for high-quality specialized services increasing significantly. The establishment of domestic science majors at higher education institutions is advancing the social status of the butler profession.",
    sections: [
      {
        heading: 'Market Overview and Business Scale',
        image: '/images/generated/jba-fs-061-news_china-butler-demand-surge-v3.jpg', aspect: 'aspect-[3/4]',
        alt: 'Private butler candidates undergoing specialized training in China',
        paragraphs: [
          "Demand for private butler services is surging among China's wealthy. According to Beijing-based training company Meiyinghui, butler services have become common in China since 2015, now managing approximately 1,000 butlers.",
          'Private butlers typically begin work after 3-5 month training sessions, with 70% of candidates being women. Despite the COVID-19 pandemic, supply has not kept up with demand over the past 2-3 years.'
        ]
      },
      {
        heading: 'Employment Patterns and Long-term Relationships',
        image: '/images/generated/jba-fs-062-news_china-butler-demand-surge-v3.jpg', aspect: 'aspect-[3/4]',
        alt: 'A private butler serving a wealthy household under a long-term bond of trust',
        paragraphs: [
          'Private butlers typically work long-term for their employers, commonly working for the same family for at least 2-3 years. This long-term employment relationship enables the building of trust and continuous service improvement that is essential to butler services.',
          'Zeng Jingjing, who has served as a private butler trainer for two years and studied in Melbourne with a business English major, comments "This is a sunrise industry," showing strong confidence in the industry\'s future.'
        ]
      },
      {
        heading: 'Growth of the Domestic Service Industry',
        image: '/images/generated/jba-fs-063-news_china-butler-demand-surge-v3.jpg', aspect: 'aspect-[3/4]',
        alt: "China's domestic service industry with a market size exceeding 1 trillion yuan",
        paragraphs: [
          "According to the National Development and Reform Commission (NDRC), China's domestic service industry market size exceeds 1 trillion yuan with over 30 million employees. However, the shortage of high-quality domestic service personnel is particularly pronounced, with an estimated shortage of over 20 million domestic workers.",
          'Demand for domestic services is no longer limited to daily chores. With the proliferation of online shopping and food delivery improving consumer convenience, demand for higher-level specialized services has become prominent, particularly in pet care, medical services, and cultural life planning services.'
        ]
      },
      {
        heading: 'Higher Education Entry and Professional Status Improvement',
        image: '/images/generated/jba-fs-064-news_china-butler-demand-surge-v3.jpg', aspect: 'aspect-[4/3]',
        alt: 'A university program establishing an academic foundation for the profession',
        paragraphs: [
          'Universities such as Jilin Agricultural University and Hebei Normal University have established domestic science majors, establishing an academic foundation for the butler profession. Highly educated domestic science majors need not necessarily become domestic service workers, with paths open to management positions such as domestic managers, community service managers, and project managers.',
          'According to the Henan Domestic Service Association, demand for these management positions accounts for about half of market demand, with domestic science instructors and vocational college specialized teachers accounting for 25% of market demand. This is an important indicator of the diversification and specialization of butler-related professions.'
        ]
      },
      {
        heading: 'New Profession Recognition and Market Diversification',
        image: '/images/generated/jba-fs-065-news_china-butler-demand-surge-v4.jpg', aspect: 'aspect-[3/4]',
        alt: 'Recognition of new specialized professions in the national classification',
        paragraphs: [
          'The Ministry of Human Resources and Social Security added approximately 20 new professions to the national occupational classification list in late July 2024, including live streaming anchors, cloud network intelligent operation and maintenance personnel, user growth operation specialists, and cultural and creative product planning operation specialists.',
          'Particularly noteworthy is the recognition of "Leisure Camp Site Butler" as a new profession, showing that the butler concept has expanded beyond traditional household services to the leisure and tourism industries. Over half of newly added professions are closely related to new quality productive forces, reflecting diversification and new demands in people\'s lives.'
        ]
      }
    ],
    comment: "The rapid growth of butler service demand in China is deeply noted by our association as an important market signal clearly showing the future potential of butler services in Asia's largest market. Particularly, the market evolution from domestic services to specialized butler services and the improvement of professional social status shown by the establishment of domestic science majors at higher education institutions demonstrate the development potential of the butler profession. The correlation between China's wealthy population growth and butler service demand suggests comparability with Japan's butler service market, and we feel the possibility of increased butler service recognition throughout the Asian region. Based on such international market trends, we will actively work toward standardization and quality improvement of butler services in the Asian region.",
    source: [
      { label: 'Source', value: 'China Daily', link: false },
      { label: 'Article URL', value: 'https://global.chinadaily.com.cn/a/202412/04/WS674fc4a6a310f1265a1d1001_2.html', link: true },
      { label: 'Access Date', value: 'October 2025', link: false }
    ],
    related: [
      { to: '/en/news/ai-hospitality-revolution', image: '/images/generated/jba-fs-066-news_china-butler-demand-surge-v4.jpg', aspect: 'aspect-[3/4]', alt: 'Related article: AI Hospitality Revolution: 5 Transformations', title: 'AI Hospitality Revolution: 5 Transformations', desc: 'The future of butler services opened by artificial intelligence' },
      { to: '/en/news/raffles-london-campaign', image: '/images/generated/jba-fs-en-009-news_china-butler-demand-surge.jpg', aspect: 'aspect-[3/4]', alt: 'Related article: Raffles "The Butler Did It" Campaign Chapter 2', title: 'Raffles "The Butler Did It" Campaign Chapter 2', desc: 'Sequel campaign set in London' }
    ]
  };
  const S = article.sections;

  const sources = [
    { label: 'Source', value: 'China Daily', link: false },
    { label: 'Original Article', value: 'https://global.chinadaily.com.cn/a/202412/04/WS674fc4a6a310f1265a1d1001_2.html', link: true },
    { label: 'Pay / Follow-up', value: 'https://www.chinadaily.com.cn/a/202506/02/WS683cea61a310a04af22c2b79.html', link: true },
    { label: 'Wealth Data', value: 'https://www.statista.com/topics/5788/millionaires-in-china/', link: true },
    { label: 'Luxury Housing', value: 'https://daxueconsulting.com/luxury-home-market-in-china/', link: true },
    { label: 'Access Date', value: 'July 2026', link: false }
  ];
  const toc = [
    { id: 's-market', label: 'Market Overview and Business Scale' },
    { id: 's-employment', label: 'Employment Patterns and Long-term Relationships' },
    { id: 's-salary', label: 'Butler Pay and the Workforce' },
    { id: 's-industry', label: 'Growth of the Domestic Service Industry' },
    { id: 's-education', label: 'Higher Education Entry and Professional Status' },
    { id: 's-wealth', label: 'The Wealthy Class and Luxury Housing Driving Demand' },
    { id: 's-newjobs', label: 'New Profession Recognition and Market Diversification' }
  ];

  return (
    <Layout>
      <title>Surge in Butler Demand Among Chinese Wealthy | Japan Butler Association</title>
      <meta name="description" content="Demand for private butlers is surging among China's wealthy, with the domestic service industry exceeding 1 trillion yuan and higher-education majors raising the profession's social status." />
      <link rel="canonical" href="https://butler-association.jp/en/news/china-butler-demand-surge" />
      <link rel="alternate" hrefLang="ja" href="https://butler-association.jp/news/china-butler-demand-surge" />
      <link rel="alternate" hrefLang="en" href="https://butler-association.jp/en/news/china-butler-demand-surge" />

      <ArticleHero eyebrow="News / Market" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="Article Highlights"
              points={[
                "Private butler demand is surging among China's wealthy, with firms managing around 1,000 butlers emerging",
                'The domestic service industry exceeds 1 trillion yuan; average pay is about 400,000 yuan and 70% of the workforce is women',
                'University domestic-science majors and new profession recognition are raising the social status of the butler profession'
              ]}
            />

            <SectionHeading id="s-market" en="Market Overview">{S[0].heading}</SectionHeading>
            <div className="space-y-6">{S[0].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
            <SourceShot
              src="/images/news-sources/china-chinadaily.jpg"
              alt="Screenshot of the China Daily article on butler demand among wealthy Chinese families"
              media="China Daily"
              url="https://global.chinadaily.com.cn/a/202412/04/WS674fc4a6a310f1265a1d1001_2.html"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="This is a sunrise industry." cite="Zeng Jingjing / private butler trainer" />
            </div>

            <SectionHeading id="s-employment" en="Employment">{S[1].heading}</SectionHeading>
            <div className="space-y-6">{S[1].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-salary" en="Pay & People">Butler Pay and the Workforce</SectionHeading>
            <div className="space-y-6">
              <p className={P}>According to follow-up reporting from China Daily, private butlers earn an average of about <Stat>400,000 yuan</Stat> (roughly $55,000) a year, ranging from 200,000 to 800,000 yuan depending on experience and skill. With demand outstripping supply, terms are shifting in favor of practitioners.</p>
              <p className={P}>About <Stat>70%</Stat> of practitioners are women, and university graduates and returnees from study abroad are increasingly entering the field. Beijing alone hosts at least five firms that manage and place butlers, and demand is spreading from major cities to second- and third-tier cities.</p>
            </div>

            <SectionHeading id="s-industry" en="Industry">{S[2].heading}</SectionHeading>
            <div className="space-y-6">{S[2].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-education" en="Education">{S[3].heading}</SectionHeading>
            <div className="space-y-6">{S[3].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-wealth" en="Wealth Base">The Wealthy Class and Luxury Housing Driving Demand</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Behind the demand lies the depth of China's wealthy class. Dollar millionaires number second only to the United States—an estimated <Stat>6.3 million</Stat>—and a younger, more globally experienced cohort is driving acceptance of butler services.</p>
              <p className={P}>The luxury housing market also underpins demand. Homes priced above 10 million yuan saw an estimated <Stat>52,000 units</Stat> sold across the top-10 cities in 2023, tying such wealth accumulation directly to demand for specialized in-home services.</p>
            </div>
            <SourceShot
              src="/images/news-sources/china-wikipedia.jpg"
              alt="Screenshot of the English Wikipedia page on the history and role of the butler profession"
              media="Wikipedia (Butler)"
              url="https://en.wikipedia.org/wiki/Butler"
            />

            <SectionHeading id="s-newjobs" en="New Professions">{S[4].heading}</SectionHeading>
            <div className="space-y-6">{S[4].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="Contents" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">Average Pay</dt><dd className="text-[#333] mt-0.5">approx. 400,000 yuan ($55K)</dd></div>
                  <div><dt className="text-[#8f7a56]">Women</dt><dd className="text-[#333] mt-0.5">approx. 70%</dd></div>
                  <div><dt className="text-[#8f7a56]">Domestic Service Market</dt><dd className="text-[#333] mt-0.5">over 1 trillion yuan</dd></div>
                  <div><dt className="text-[#8f7a56]">Dollar Millionaires</dt><dd className="text-[#333] mt-0.5">approx. 6.3M</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="Comment from Japan Butler Association" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="Source" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="Related Articles" items={article.related} readLabel="Read" backHref="/en/news" backLabel="Back to News List" />
    </Layout>
  );
}
